/**
 * Legal re-consent: does the consent we have on file still cover the CURRENT
 * legal text?
 *
 * The server hashes the canonical documents (cloudflare/src/lib/legalVersion.ts)
 * and exposes the active hash at GET /api/legal/current. An account stores the
 * hash it last accepted in `accounts.legal_version_hash`, surfaced to the client
 * on /api/settings/all.
 *
 * Previously nothing compared the two: `legalAcknowledged` was set from the
 * presence of a `legalAcknowledgedAt` timestamp alone, so once a user had ever
 * accepted, they were never asked again — no matter how the documents changed.
 * ToS §16 promises 14 days' notice of material changes; this is the mechanism
 * that delivers it.
 */
import { apiRequest } from "@/lib/queryClient";

export interface LegalCurrent {
  hash: string;
  text: string;
}

/**
 * Whether the stored consent satisfies the current version.
 *
 * Fails CLOSED on a missing acknowledgement (never consented -> must consent),
 * but OPEN on an unknown current hash: if we could not reach the server we do
 * not know that anything changed, and blocking the app behind a consent splash
 * on every offline launch would be worse than deferring the prompt to the next
 * successful load.
 */
export function isConsentCurrent(input: {
  acknowledgedAt: number | null | undefined;
  acknowledgedHash: string | null | undefined;
  currentHash: string | null | undefined;
}): boolean {
  if (!input.acknowledgedAt) return false;
  // Server hash unknown (offline / fetch failed): trust the existing ack.
  if (!input.currentHash) return true;
  // Acknowledged before hashes were stored: treat as stale so the user sees the
  // current text once, rather than being grandfathered onto unknown terms.
  if (!input.acknowledgedHash) return false;
  return input.acknowledgedHash === input.currentHash;
}

/** Read the server's active legal version. Returns null when unreachable. */
export async function fetchLegalCurrent(): Promise<LegalCurrent | null> {
  try {
    const res = await apiRequest("GET", "/api/legal/current");
    const body = (await res.json()) as Partial<LegalCurrent>;
    if (typeof body?.hash !== "string") return null;
    return { hash: body.hash, text: typeof body.text === "string" ? body.text : "" };
  } catch {
    return null;
  }
}

/**
 * Record acceptance of `hash` server-side. Writes both
 * `accounts.legal_version_hash` and `account_settings.legal_acknowledged_at`,
 * so this is the single call that persists consent.
 *
 * Returns false if the server rejected the hash as stale (426) or the request
 * failed — the caller keeps local state optimistic either way, and the next
 * hydrate re-checks.
 */
export async function acknowledgeLegalVersion(hash: string | null): Promise<boolean> {
  try {
    await apiRequest("POST", "/api/legal/acknowledge", hash ? { hash } : {});
    return true;
  } catch {
    return false;
  }
}
